import React from 'react';

// Design System Components
import Tag from 'Components/DesignSystem/Tag/Tag';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Cart } from 'Components/DesignSystem/Iconography/Icons';

// Constants
import {
  DEFAULT_CTA_CONFIGURATION,
  DEFAULT_DESCRIPTION_STYLE,
  PREVIEW_PRODUCT,
  STRINGS,
  formatPrice,
} from '../../../constants';

// i18n — every number and every sentence in this file goes through one of these.
import { formatDecimal, formatNumber, formatPercent, interpolate, plural } from '../../../i18n';

// Types
import { FieldId, FieldConfiguration } from '../../../types';

interface FieldBlockContentProps {
  fieldId: FieldId;
  configuration: FieldConfiguration;
}

/**
 * A field's Display label prefixes its value; an empty label renders the value
 * on its own (spec R43).
 */
const withLabel = (label: string, value: string): string =>
  // The `: ` separator is part of the message, not punctuation this component
  // owns — several locales set it differently, and one uses a full-width colon.
  label.trim() === ''
    ? value
    : interpolate(STRINGS.preview.product.labelledValue, { label, value });

const product = PREVIEW_PRODUCT;

/**
 * The unit-of-measure suffix a price row carries while its OWN configuration asks
 * for it (spec R69, per element since 14021) — the prototype's `/ Each`, or nothing.
 */
const uomSuffix = (showUom: boolean): string =>
  showUom
    ? interpolate(STRINGS.preview.product.uomSuffix, { unit: product.unitOfMeasure })
    : '';

const stars = (rating: number): string => '★'.repeat(Math.round(rating));
const dimStars = (rating: number): string => '★'.repeat(Math.max(0, 5 - Math.round(rating)));

/**
 * Where a part-width CTA button sits in its row.
 *
 * The CTA block fills its row so its width percentage means something (R70), which
 * means text-align cannot place it — the button is a flex item, so the row's
 * justification does.
 */
const ctaJustifyClass: Record<FieldConfiguration['alignment'], string> = {
  left: 'tw-justify-start',
  center: 'tw-justify-center',
  right: 'tw-justify-end',
};

/**
 * The visual for one field on the preview card.
 *
 * Everything here is a **static facsimile** of a storefront control: the CTA and
 * the quantity selector render as styled text rather than as a real `<button>`
 * or `<input>`. A focusable control nested inside a draggable, clickable block
 * would fight both click-to-select (R55) and the keyboard drag handle (R10), and
 * the preview is a mock of the shopper's card rather than a working one.
 *
 * Alignment is applied by the wrapping block, not here (spec R44). So is the
 * element's own selector class, so a user's Custom styling reaches this content
 * (spec R67).
 *
 * There is deliberately **no `uom` arm**: the "Show UOM on prices" field renders no
 * block of its own (spec R72) — `BLOCKLESS_FIELD_IDS` keeps it from ever getting a
 * placement, and its whole effect is the suffix the price arms below apply.
 */
const FieldBlockContent = ({ fieldId, configuration }: FieldBlockContentProps) => {
  const label = configuration.displayLabel;
  // Per element since 14021 — read from this field's own configuration rather than
  // threaded down from the card, which is what made one panel change every price.
  const unit = uomSuffix(configuration.showUom === true);
  // Price fields only (14021). Derived once so the five price arms cannot drift in
  // how they apply it; a field that never offers the toggle simply never reads it.
  const strike = configuration.strikethrough === true ? ' tw-line-through' : '';

  switch (fieldId) {
    case 'sku':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          {withLabel(label, product.sku)}
        </Typography>
      );

    case 'title':
      return (
        <Typography variant="title-header" as="span" className="tw-text-ink">
          {withLabel(label, product.title)}
        </Typography>
      );

    case 'brand':
      return (
        <Typography variant="sub-body-supporting" as="span" className="tw-text-ink-tertiary">
          {product.brand}
        </Typography>
      );

    case 'model':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          {withLabel(label, product.modelNumber)}
        </Typography>
      );

    case 'desc': {
      // Clamp 2 lines / Full / Collapsed (spec R71). "Collapsed" is the
      // prototype's affordance rather than the text: a line the shopper expands.
      const descStyle = configuration.descStyle ?? DEFAULT_DESCRIPTION_STYLE;
      if (descStyle === 'collapsed') {
        return (
          <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
            {`${STRINGS.preview.descriptionCollapsed} ⌄`}
          </Typography>
        );
      }
      return (
        <Typography
          variant="body-primary"
          as="span"
          className={`tw-text-ink-secondary tw-break-words ${
            descStyle === 'partly' ? 'tw-line-clamp-2' : ''
          }`}
        >
          {product.shortDescription}
        </Typography>
      );
    }

    case 'listPrice':
      return (
        <Typography variant="title-header" as="span" className={`tw-text-ink${strike}`}>
          {`${withLabel(label, formatPrice(product.listPrice))}${unit}`}
        </Typography>
      );

    case 'salePrice':
      // Renders the SALE price and nothing else (14021).
      //
      // It used to render a second value as well: the list price, hard-coded with a
      // strike through it, whether or not the card had List Price enabled. That made
      // the strike an unremovable property of this arm rather than a setting, showed
      // a price the merchandiser had not asked for, and duplicated List Price when
      // both fields were on. The strike is now `strikethrough` on THIS field's
      // configuration, and the list price is List Price's own arm.
      return (
        <Typography
          variant="title-header"
          as="span"
          className={`tw-text-tone-danger-ink${strike}`}
        >
          {`${withLabel(label, formatPrice(product.salePrice))}${unit}`}
        </Typography>
      );

    case 'savingsAmt':
      // Takes the suffix like every other CURRENCY row (R69). It is one of the four
      // fields `PROPERTY_SECTIONS` gives a Pricing section, so its panel offers the
      // UOM checkbox — and until 2026-09-28 that checkbox did nothing here, which is
      // the one thing a control must never do. `savingsPct` is the deliberate
      // exception: a percentage takes no unit, which is why it declares no Pricing
      // section and never reaches this suffix.
      return (
        <Typography
          variant="sub-title-label"
          as="span"
          className={`tw-text-tone-success-ink${strike}`}
        >
          {`${withLabel(label, formatPrice(product.savingsAmount))}${unit}`}
        </Typography>
      );

    case 'savingsPct':
      return (
        <Typography
          variant="sub-title-label"
          as="span"
          className={`tw-text-tone-success-ink${strike}`}
        >
          {withLabel(label, formatPercent(product.savingsPercent))}
        </Typography>
      );

    case 'pricePerUnit':
      // This row takes the same per-field UOM suffix as the other prices, so it
      // stops being the one place that shows "/ Each" unasked (R69).
      return (
        <Typography
          variant="caption-metadata"
          as="span"
          className={`tw-text-ink-secondary${strike}`}
        >
          {`${withLabel(
            label,
            interpolate(STRINGS.preview.product.pricePerUnit, {
              price: formatPrice(product.pricePerUnit),
            })
          )}${unit}`}
        </Typography>
      );

    case 'currency':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          {withLabel(label, product.currency)}
        </Typography>
      );

    case 'stock':
      return (
        <Tag label={product.stockLabel} tone={product.inStock ? 'success' : 'warning'} showToneGlyph />
      );

    case 'leadTime':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          {withLabel(label, product.leadTime)}
        </Typography>
      );

    case 'qtyAvail':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          {withLabel(label, formatNumber(product.quantityAvailable))}
        </Typography>
      );

    case 'discontinued':
      return (
        <Typography variant="sub-title-label" as="span" className="tw-text-tone-danger-ink">
          {product.discontinued
            ? STRINGS.preview.product.discontinued
            : STRINGS.preview.product.notDiscontinued}
        </Typography>
      );

    case 'starRating':
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <Typography variant="sub-title-label" as="span" className="tw-text-yellow-warning-500">
            {stars(product.rating)}
            {/* contrast-ok(non-text): the UNFILLED half of the star bar. Its
                faintness is what distinguishes it from the filled stars, and
                the numeric rating is rendered beside it in grey-700 — so the
                value never depends on reading these. */}
            <span className="tw-text-edge">{dimStars(product.rating)}</span>
          </Typography>
          <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
            {formatDecimal(product.rating, 1)}
          </Typography>
        </span>
      );

    case 'reviewCount':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-tertiary">
          {plural(product.reviewCount, STRINGS.preview.product.reviewCount)}
        </Typography>
      );

    case 'moq':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          {withLabel(label, formatNumber(product.minimumOrderQuantity))}
        </Typography>
      );

    case 'qtyIncrement':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          {withLabel(label, formatNumber(product.quantityIncrement))}
        </Typography>
      );

    case 'preferred':
      return <Tag label={STRINGS.preview.product.preferred} tone="info" showToneGlyph />;

    case 'badge':
      return (
        <Tag label={product.badge} tone="warning" />
      );

    case 'promoMsg':
      return (
        <Typography variant="sub-title-label" as="span" className="tw-text-tone-success-ink">
          {product.promotionalMessage}
        </Typography>
      );

    case 'newArrival':
      return (
        <Tag label={STRINGS.preview.product.newArrival} tone="success" />
      );

    case 'bestSeller':
      return (
        <Tag label={interpolate(STRINGS.preview.product.bestSeller, {
            rank: formatNumber(product.bestSellerRank),
          })} tone="info" />
      );

    case 'sponsored':
      return (
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-tertiary tw-uppercase">
          {STRINGS.preview.product.sponsored}
        </Typography>
      );

    case 'cta': {
      // The Button section's options (spec R70). Width and height are user-chosen
      // numbers, so they are inline style rather than utility classes: Tailwind
      // generates classes from source it can read, and a class built at runtime
      // would be a silent no-op (R4).
      const cta = configuration.cta ?? DEFAULT_CTA_CONFIGURATION;
      return (
        <span className={`tw-flex tw-w-full ${ctaJustifyClass[configuration.alignment]}`}>
          <span
            style={{
              width: cta.fullWidth ? '100%' : `${cta.widthPct}%`,
              height: `${cta.heightPx}px`,
            }}
            className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-4 tw-rounded-sm tw-bg-accent-bold tw-text-ink-inverse tw-text-title tw-font-medium"
          >
            {cta.showIcon && <Cart />}
            {cta.showText && (cta.label.trim() === '' ? product.ctaLabel : cta.label)}
          </span>
        </span>
      );
    }

    case 'qtySelector':
      return (
        <span className="tw-inline-flex tw-items-center tw-border tw-border-edge tw-rounded-sm tw-overflow-hidden">
          <span className="tw-px-2 tw-py-1 tw-text-ink-tertiary tw-text-body-primary">−</span>
          <span className="tw-px-3 tw-py-1 tw-text-ink tw-text-body-primary tw-border-l tw-border-r tw-border-edge">
            1
          </span>
          <span className="tw-px-2 tw-py-1 tw-text-ink-tertiary tw-text-body-primary">+</span>
        </span>
      );

    case 'image':
    default:
      // The Primary Image is the photo background, not a block.
      return null;
  }
};

export default FieldBlockContent;
